/**
 * `ChatHeader` — the strip above the transcript with the chat title,
 * a refresh button (re-pulls the threads list), and an overflow menu.
 *
 * The `title` prop accepts either a string or a ReactNode so the
 * orchestrator can pass a typewriter-animated fragment while a streamed
 * title is being rendered character-by-character.
 */
import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  MessageCircleIcon,
  MoreHorizontalIcon,
  RefreshCwIcon,
  Share2Icon,
  Trash2Icon,
} from "lucide-react";
import {
  Button,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRoot,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@flint/ui";

interface ChatHeaderProps {
  title: ReactNode;
  icon?: string | null;
  onRefresh: () => void;
  onClear: () => void;
  onShare?: () => void;
}

export function ChatHeader({ title, icon, onRefresh, onClear, onShare }: ChatHeaderProps) {
  return (
    <header className="relative z-10 flex shrink-0 items-center justify-between gap-2 overflow-hidden bg-paper/95 px-3 py-2 backdrop-blur lg:p-3">
      <div className="flex min-w-0 items-center gap-2">
        <Link
          to="/ask"
          className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-muted transition-colors hover:bg-bg hover:text-ink"
          aria-label="Back to all chats"
        >
          <ArrowLeftIcon className="h-4 w-4" aria-hidden />
        </Link>
        {icon ? (
          <span className="shrink-0 text-base leading-none" aria-hidden>{icon}</span>
        ) : (
          <MessageCircleIcon className="h-5 w-5 shrink-0 text-ink-2" aria-hidden />
        )}
        <h1 className="min-w-0 truncate text-sm font-semibold text-ink">{title}</h1>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Button
          intent="ghost"
          className="h-8 w-8 p-0"
          onClick={onRefresh}
          aria-label="Refresh chats"
        >
          <RefreshCwIcon className="h-4 w-4" aria-hidden />
        </Button>
        <DropdownMenuRoot>
          <DropdownMenuTrigger asChild>
            <Button intent="ghost" className="h-8 w-8 p-0" aria-label="More chat options">
              <MoreHorizontalIcon className="h-4 w-4" aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem
              onSelect={() =>
                onShare ? onShare() : void navigator.clipboard?.writeText(window.location.href)
              }
            >
              <Share2Icon className="h-4 w-4" aria-hidden />
              Share
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-bad" onSelect={onClear}>
              <Trash2Icon className="h-4 w-4" aria-hidden />
              Clear current chat
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenuRoot>
      </div>
    </header>
  );
}
